import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeAlert } from "../redux/slices/alerts";
import { newRegistration, setNewRegistration } from "../redux/slices/auth";
import { useNavigate } from "react-router-dom";
import { setNewUser, setNewClient, setNewAccount, setNewDocument, checkEmailAvailability } from "../redux/slices/register";
import RegisterStep1 from "../components/RegisterStep1";
import RegisterStep2 from "../components/RegisterStep2";
import RegisterStep3 from "../components/RegisterStep3";

const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentStep = useSelector((state) => state.auth.newRegistration);
    const user = useSelector((state) => state.register.user);
    const client = useSelector((state) => state.register.client);
    const account = useSelector((state) => state.register.account);
    const [validating, setValidating] = useState(false);
    
    const [step2FormData, setStep2FormData] = useState({
        address: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        website: "",
        email: "",
        phone: "",
    });

    const [formData, setFormData] = useState({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        password: user.password || "",
        title: user.title || "",
        client: client.name || "",
    });
    const [formErrors, setFormErrors] = useState({});

    const validateEmailAvailability = (value) => {
        if (!value) return false;
        
        setValidating(true);
        dispatch(
            checkEmailAvailability({
                email: value,
                callback: (status) => {
                    if (status === 0) {
                        setFormErrors((prev) => ({ ...prev, email: "Email not Available" }));
                    } else {
                        setFormErrors((prev) => ({ ...prev, email: "" }));
                    }
                    setValidating(false);
                },
            })
        );
        return true;
    }

    const validateField = (name, value) => {
        switch (name) {
            case "first_name":
            case "last_name":
                return value?.trim() === "" ? "This field is required" : "";
            case "email":
                if (value?.trim() === "") return "Email is required";
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) return "Invalid email format";
                validateEmailAvailability(value);
                return formErrors.email || "";
            case "password":
                if (value?.trim() === "") return "Password is required";
                if (value?.length < 8) return "Password must be at least 8 characters";
                return "";
            case "title":
                return value?.trim() === "" ? "Job title is required" : "";
            case "client":
                return value?.trim() === "" ? "Company name is required" : "";
            case "address":
                return value?.trim() === "" ? "Address is required" : "";
            case "city":
                return value?.trim() === "" ? "City is required" : "";
            case "state":
                return value?.trim() === "" ? "State is required" : "";
            case "zip":
                return value?.trim() === "" ? "Zip code is required" : "";
            case "country":
                return value?.trim() === "" ? "Country is required" : "";
            case "website":
                return value?.trim() === "" ? "Website is required" : "";
            case "email":
                return value?.trim() === "" ? "Email is required" : "";
            case "phone":
                return value?.trim() === "" ? "Phone is required" : "";
            default:
                return "";
        }
    };

    const validateAllFields = () => {
        const errors = {};
        let isValid = true;

        Object.keys(formData).forEach((name) => {
            const error = validateField(name, formData[name]);
            if (error) {
                errors[name] = error;
                isValid = false;
            }
        });

        setFormErrors(errors);
        return isValid;
    };
    

    const stopNewRegistration = (event) => {
        dispatch(setNewRegistration({ step: 0 }));
    };

    const newRegistrationStep1 = (event) => {
        dispatch(setNewRegistration({ step: 1 }));
    };

    const newRegistrationStep2 = (event) => {
        event.preventDefault();

        if (!validateAllFields()) {
            return;
        }

        const { client: clientName, ...userData } = formData;
        
        // Set user data
        dispatch(setNewUser({ user: { ...user, ...userData } }));
        
        // Set client and account with matching names and proper billing terms
        dispatch(setNewClient({ 
            client: { 
                ...client, 
                name: clientName,
                billingTerms: { 
                    type: 'Hourly',
                    billingEntity: true 
                }
            } 
        }));
        dispatch(setNewAccount({ 
            account: { 
                ...account, 
                name: clientName
            } 
        }));
        
        dispatch(setNewRegistration({ step: 2 }));
    };

    const newRegistrationStep3 = (event) => {
        dispatch(setNewClient({
            client: {
                ...client,
                address: step2FormData.address,
                city: step2FormData.city,
                state: step2FormData.state,
                zip: step2FormData.zip,
                country: step2FormData.country,
                website: step2FormData.website,
                email: step2FormData.email,
                phone: step2FormData.phone,
            }
        }));
        
        dispatch(setNewRegistration({ step: 3 }));
    };

    const handleRegister = (event) => {
        dispatch(newRegistration({ client, user, account, callback: () => dispatch(setNewRegistration({ step: 0 })) }));
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <RegisterStep1
                        formData={formData}
                        setFormData={setFormData}
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                        validateField={validateField}
                        newRegistrationStep2={newRegistrationStep2}
                        stopNewRegistration={stopNewRegistration}
                    />
                );
            case 2:
                return (
                    <RegisterStep2 
                        newRegistrationStep1={newRegistrationStep1} 
                        newRegistrationStep3={newRegistrationStep3} 
                        formData={step2FormData} 
                        setFormData={setStep2FormData} 
                        formErrors={formErrors} 
                        setFormErrors={setFormErrors} 
                        validateField={validateField} 
                    />
                );
            case 3:
                return <RegisterStep3 newRegistrationStep2={newRegistrationStep2} handleRegister={handleRegister} clientName={client.name} />;
            default:
                return null;
        }
    };

    return renderStep();
};

export default Register;
